import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { Instance } from 'mobx-state-tree';
import { inject } from 'mobx-react';
import * as React from 'react';
import { Button, ButtonGroup, Colors, InputGroup, Intent } from '@blueprintjs/core';
import { MAP, RESET, SEND_MESSAGE } from '@blueprintjs/icons/lib/esm/generated/iconNames';
import { Page, TLCode } from '../../common/enums';
import BaseGridXL from '../common/grid/BaseGridXL';
import styled from '@emotion/styled';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import SimpleTextFormContent from '../common/form/SimpleTextFormGroup';
import EditCell from '../common/grid/EditCell';
import { Pharmacy } from '../../store/models/Pharmacy';
import { AuthenticationStore, SecretariatStore, UserStore, GroupStore } from '../../store';
import { Select } from 'antd';
import { MessageDialog } from '../common/MessageDialog';
import { MessageStore } from '../../store/MessageStore';

interface PharmaciesListProps extends WithTranslateProps {
  deletePharmacy: (data: any) => void;
  filter: string;
  isLoading: boolean;
  navigate: (url: string) => void;
  pharmaciesList: Array<Instance<typeof Pharmacy>>;
  setFilterText: (filter: string) => void;
  showOnMap: () => void;
  reloadPharmacyList: () => void;
  activeFilter: string;
  levelFilter: string;
  groupFilter: string;
  secretariatFilter: number | undefined;
  setActiveFilter: (filter: string) => void;
  setLevelFilter: (filter: string) => void;
  setGroupFilter: (filter: string) => void;
  setTextFilter: (filter: string) => void;
  setSecretariatFilter: (filter: number | undefined) => void;
  UserStore?: Instance<typeof UserStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  GroupStore?: Instance<typeof GroupStore>;
  MessageStore?: Instance<typeof MessageStore>;
}

interface PharmacyListState {
  currentGroupFilter: string;
  isMessageDialogOpen: boolean;
}

export function renderStatus(params: ICellRendererParams) {
  const { value } = params;
  return value ? '' : 'non';
}

@inject('UserStore')
@inject('AuthenticationStore')
@inject('SecretariatStore')
@inject('GroupStore')
@inject('MessageStore')
class PharmaciesList extends React.Component<PharmaciesListProps, PharmacyListState> {
  state = {
    currentGroupFilter: '',
    isMessageDialogOpen: false,
  };

  async componentDidMount() {
    await this.props.GroupStore!.checkListLoaded();
    await this.props.UserStore!.checkListLoaded();
  }

  renderPharmacyUser = (params: ICellRendererParams) => {
    const user = this.props.UserStore!.dataList.find((user) => user.userName == params.data.apbNumber);
    return user ? user!.firstName! + ' ' + user!.lastName! : 'unknown';
  };

  private onRowDoubleClicked = (ev) => {
    this.props.navigate(`${Page.PharmacyPage}/${ev.data.pharmacyId}`);
  };

  private getColumnDefs = (): ColDef[] => {
    const { translate, navigate, deletePharmacy } = this.props;
    return [
      {
        headerName: '',
        field: 'level',
        width: 20,
        filter: false,
        sortable: true,
      },
      {
        headerName: translate(TLCode.PHARMACIES_ApbNum),
        field: 'apbNumber',
        width: 40,
      },
      {
        headerName: translate(TLCode.PHARMACIES_PharmacyName),
        field: 'name',
        resizable: true,
      },
      {
        headerName: translate(TLCode.PHARMACIES_Address),
        field: 'address',
        width: 160,
        resizable: true,
      },
      {
        headerName: translate(TLCode.PHARMACIES_ZipCode),
        field: 'zipCode',
        width: 31,
      },
      {
        headerName: translate(TLCode.PHARMACIES_City),
        field: 'city',
        width: 100,
      },
      {
        headerName: translate(TLCode.PHARAMCIES_Secretariat),
        field: 'secretariat',
        width: 80,
      },
      {
        headerName: translate(TLCode.PHARMACIES_Group),
        field: 'group',
        width: 40,
        resizable: true,
      },
      {
        headerName: 'Titulaire',
        field: 'userId',
        width: 90,
        cellRenderer: this.renderPharmacyUser,
      },
      {
        headerName: translate(TLCode.PHARMACIES_Active),
        field: 'status',
        width: 40,
        cellRendererFramework: renderStatus,
      },
      {
        headerName: '',
        width: 20,
        cellRendererFramework: EditCell,
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            onEditButtonClick: () => {
              navigate(`${Page.PharmacyPage}/${params.data.pharmacyId}`);
            },
          };
        },
      },
    ];
  };

  buildGridData = () => {
    const { activeFilter, levelFilter, groupFilter, secretariatFilter } = this.props;
    return this.props.pharmaciesList
      .filter((elem) => {
        let show = true;
        if (activeFilter === 'active' && !elem.isActive) {
          show = false;
        } else if (activeFilter === 'inactive' && elem.isActive) {
          show = false;
        } else if (levelFilter === 'three' && (elem.level == null || elem.level !== 3)) {
          show = false;
        } else if (levelFilter === 'one' && (elem.level == null || elem.level !== 1)) {
          show = false;
        } else if (levelFilter === 'zero' && (elem.level == null || elem.level !== 0)) {
          show = false;
        } else if (
          groupFilter != '' &&
          ((elem.groupId !== undefined &&
            !groupFilter.includes(this.props.GroupStore!.dataList.find((g) => g.groupId === elem.groupId!)!.name)) ||
            elem.groupId === undefined)
        ) {
          show = false;
        } else if (
          secretariatFilter !== 0 &&
          secretariatFilter !== undefined &&
          elem.secretariatId !== secretariatFilter
        )
          show = false;

        return show;
      })
      .map((p) => ({
        pharmacyId: p.pharmacyId,
        apbNumber: p.apbNumber,
        name: p.name,
        phone: p.phone,
        address: p.address,
        zipCode: p.zipCode,
        city: p.city,
        level: p.level,
        status: p.isActive,
        zone: p.zoneId,
        density: p.density,
        anchor: p.anchor,
        group:
          p.groupId === undefined ? '' : this.props.GroupStore!.dataList.find((g) => g.groupId === p.groupId!)!.name,
        secretariatId: p.secretariatId,
        secretariat:
          p.secretariatId === undefined
            ? ''
            : this.props.SecretariatStore!.dataList.find((s) => s.secretariatId === p.secretariatId)!.code,
      }));
  };

  handleClearGroupFilterClick = () => {
    this.props.setGroupFilter('');
    this.setState({ currentGroupFilter: '' });
  };

  renderStatus = (row) => {
    return row.data.status ? '' : 'non';
  };

  renderPharmacist = (row) => {
    const user = this.props.UserStore!.dataList.find((user) => user.userName == row.data.apbNumber);
    return user ? user!.firstName! + ' ' + user!.lastName! : 'unknown';
  };

  newMessage = () => {
    this.setState({ isMessageDialogOpen: true });
  };

  saveMessage = async (data: any) => {
    this.setState({ isMessageDialogOpen: false });
    await this.props.MessageStore!.sendMessage(data);
  };

  getSecretariatId = () => {
    if (this.props.AuthenticationStore!.isSuperAdmin)
      return this.props.SecretariatStore!.selectedSecretariatId === 0
        ? undefined
        : this.props.SecretariatStore!.selectedSecretariatId;
    if (this.props.AuthenticationStore!.isSecretariat) return this.props.AuthenticationStore!.secretariatId!;
    return undefined;
  };

  render() {
    const {
      setFilterText,
      filter,
      showOnMap,
      reloadPharmacyList,
      isLoading,
      setActiveFilter,
      setLevelFilter,
      setGroupFilter,
      setTextFilter,
      translate,
    } = this.props;

    const clearButton = <Button icon={'cross'} minimal={true} onClick={this.handleClearGroupFilterClick} />;
    const secretariatList = this.props.SecretariatStore!.dataList;
    const secretariatFilter = this.props.secretariatFilter;
    return (
      <Container>
        <MessageDialog
          isOpen={this.state.isMessageDialogOpen}
          onClose={() => this.setState({ isMessageDialogOpen: false })}
          isTest={false}
          save={this.saveMessage}
          pharmacyId={undefined}
          secretariatId={this.getSecretariatId()}
          pharmacyList={this.props.pharmaciesList}
          groupList={this.props.GroupStore!.dataList}
          translate={translate}
          isSecretariat={this.props.AuthenticationStore!.isSecretariat}
        />
        <DivLine>
          <DivLeft style={{ width: '300px' }}>
            <div className="bp3-input-group .modifier" style={{ width: '300px' }}>
              <span className="bp3-icon bp3-icon-search" />
              <input
                type="text"
                className="bp3-input"
                placeholder="Search"
                value={filter}
                onChange={(e) => setTextFilter(e.target.value)}
                // onChange={(e) => setFilterText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.charCode == 13) {
                    const apbNr = parseInt(filter);
                    this.props.navigate(
                      `${Page.PharmacyPage}/${
                        this.props.pharmaciesList[this.props.pharmaciesList.findIndex((ph) => ph.apbNumber == apbNr)]
                          .pharmacyId
                      }`,
                    );
                  }
                  return false;
                }}
              />
            </div>
          </DivLeft>
          <DivCenter style={{ width: '100%' }}>
            <SimpleTextFormContent label={TLCode.PHARMACIES_ActivityStatus}>
              <ButtonGroup>
                <Button
                  text={translate(TLCode.PHARMACIES_Active)}
                  onClick={() => setActiveFilter('active')}
                  active={this.props.activeFilter === 'active'}
                />
                <Button
                  text={translate(TLCode.PHARMACIES_Inactive)}
                  onClick={() => setActiveFilter('inactive')}
                  active={this.props.activeFilter === 'inactive'}
                />
                <Button
                  text={translate(TLCode.PHARMACIES_All_M)}
                  onClick={() => setActiveFilter('both')}
                  active={this.props.activeFilter === 'both'}
                />
              </ButtonGroup>
            </SimpleTextFormContent>
            <SimpleTextFormContent label={TLCode.PHARMACIES_Level}>
              <ButtonGroup>
                <Button
                  text={translate(TLCode.PHARMACIES_Three)}
                  onClick={() => setLevelFilter('three')}
                  active={this.props.levelFilter === 'three'}
                />
                <Button
                  text={translate(TLCode.PHARMACIES_One)}
                  onClick={() => setLevelFilter('one')}
                  active={this.props.levelFilter === 'one'}
                />
                <Button
                  text={translate(TLCode.PHARMACIES_Zero)}
                  onClick={() => setLevelFilter('zero')}
                  active={this.props.levelFilter === 'zero'}
                />
                <Button
                  text={translate(TLCode.PHARMACIES_All_M)}
                  onClick={() => setLevelFilter('all')}
                  active={this.props.levelFilter === 'all'}
                />
              </ButtonGroup>
            </SimpleTextFormContent>
            <SimpleTextFormContent label={TLCode.PHARMACIES_Groups}>
              <InputGroup
                placeholder="nom(s) séparés par ','"
                value={this.props.groupFilter}
                onChange={(e) => {
                  setGroupFilter(e.target.value);
                }}
                rightElement={clearButton}
              />
            </SimpleTextFormContent>
          </DivCenter>
          <DivRight>
            <Button
              icon={MAP}
              onClick={showOnMap}
              text={this.props.translate(TLCode.DutiesShowWideMap_Short)}
              style={{ width: '80px' }}
            />
            <Button
              icon={RESET}
              onClick={reloadPharmacyList}
              text={this.props.translate(TLCode.PHARMACIES_LoadList)}
              style={{ width: '140px' }}
            />
            <Button
              icon={SEND_MESSAGE}
              text={translate(TLCode.MESSAGE_NewMessage)}
              intent={Intent.PRIMARY}
              onClick={() => this.newMessage()}
              style={{ width: '170px' }}
              hidden={
                !(
                  this.props.AuthenticationStore!.isSecretariat ||
                  this.props.AuthenticationStore!.isAdmin ||
                  this.props.AuthenticationStore!.isSuperAdmin
                )
              }
            />
          </DivRight>
        </DivLine>
        <DivLine>
          <DivCenter>
            <SimpleTextFormContent label={TLCode.PHARAMCIES_Secretariats}>
              <DivLine>
                <div style={{ width: '305px' }}>
                  <Select
                    defaultValue={
                      this.props.secretariatFilter === 0 ||
                      secretariatList === undefined ||
                      secretariatList.length === 0
                        ? undefined
                        : this.props.secretariatFilter === undefined
                        ? secretariatList.sort((s) => s.secretariatId)[0].code
                        : secretariatList.find((s) => s.secretariatId === this.props.secretariatFilter)!.code
                    }
                    style={{ width: 300 }}
                    onChange={(code) => {
                      const selectedSecretariatId =
                        code === 'All'
                          ? undefined
                          : (secretariatList.find((s) => s.code === code)!.secretariatId as number);
                      this.props.setSecretariatFilter(selectedSecretariatId);
                    }}
                    // value={this.props.secretariatFilter === undefined
                    //   ? secretariatList.sort((s) => s.secretariatId)[0].code
                    //   : secretariatList.find((s) => s.secretariatId === this.props.secretariatFilter)!.code}
                  >
                    {secretariatList &&
                      secretariatList
                        .map((sec) => (
                          <Select.Option key={sec.code} value={sec.code}>
                            {sec.code}
                          </Select.Option>
                        ))
                        .concat(
                          <Select.Option key={'All'} value={'All'}>
                            {'All'}
                          </Select.Option>,
                        )}
                  </Select>
                </div>
              </DivLine>
            </SimpleTextFormContent>
          </DivCenter>
        </DivLine>
        <BaseGridXL
          keyName="pharmacyId"
          columnDefs={this.getColumnDefs()}
          rowData={this.buildGridData()}
          loading={isLoading}
          quickFilterText={filter}
          onRowDoubleClicked={this.onRowDoubleClicked}
          suppressClickEdit={true}
          getRowStyle={(params) => {
            if (
              params.data !== undefined &&
              this.props.AuthenticationStore!.isSecretariat &&
              (!params.data.secretariatId ||
                (params.data.secretariatId &&
                  params.data.secretariatId !== this.props.AuthenticationStore!.secretariatId))
            )
              return { 'background-color': Colors.LIGHT_GRAY1 };
            return { 'background-color': Colors.WHITE };
          }}
        />
      </Container>
    );
  }
}

export default withTranslate(PharmaciesList);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;

const DivCenter = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 5px;
  .bp3-form-content > div {
    display: flex;
    flex-direction: row;
    margin-right: 20px;
    .bp3-control {
      margin-top: 0;
      margin-right: 5px;
    }
  }
  .bp3-form-group {
    margin-bottom: 0;
  }
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 5px;
  .bp3-button {
    margin-right: 3px;
  }
`;
